'use client'

import React, { useState, useEffect, useCallback, useMemo, useId } from 'react'
import { cn } from '@/lib/utils'
import type { TOCItem } from '../../../contentlayer.config'

interface TableOfContentsProps {
  headings: TOCItem[]
  className?: string
}

export function TableOfContents({ headings, className }: TableOfContentsProps) {
  const [activeId, setActiveId] = useState<string>('')
  const [isExpanded, setIsExpanded] = useState<boolean>(true)
  const [isDesktop, setIsDesktop] = useState<boolean>(false)
  const listId = useId()

  useEffect(() => {
    if (typeof window === 'undefined') return
    const mediaQuery = window.matchMedia('(min-width: 1280px)')
    const update = () => {
      const matches = mediaQuery.matches
      setIsDesktop(matches)
      setIsExpanded(prev => (matches ? true : prev))
    }
    update()
    mediaQuery.addEventListener('change', update)
    return () => mediaQuery.removeEventListener('change', update)
  }, [])

  const h2Headings = useMemo(
    () => headings.filter(h => h.level === 2),
    [headings]
  )

  const getAnchorOffset = useCallback(() => {
    if (typeof window === 'undefined') return 120
    const cssOffset = getComputedStyle(
      document.documentElement
    ).getPropertyValue('--anchor-offset')
    const parsed = parseInt(cssOffset, 10)
    return Number.isNaN(parsed) ? 120 : parsed
  }, [])

  const scrollToId = useCallback(
    (id: string, smooth = true) => {
      const elements = document.querySelectorAll(`#${CSS.escape(id)}`)
      if (elements.length === 0) return false
      const el = elements[elements.length - 1] as HTMLElement
      const anchorOffset = getAnchorOffset() + 10
      const target =
        el.getBoundingClientRect().top + window.scrollY - anchorOffset
      window.scrollTo({
        top: Math.max(target, 0),
        behavior: smooth ? 'smooth' : 'auto',
      })
      return true
    },
    [getAnchorOffset]
  )

  useEffect(() => {
    if (h2Headings.length === 0) return
    const onHash = () => {
      const hash = window.location.hash.slice(1)
      if (hash) setActiveId(hash)
    }
    if (window.location.hash) setActiveId(window.location.hash.slice(1))
    window.addEventListener('hashchange', onHash)
    return () => window.removeEventListener('hashchange', onHash)
  }, [h2Headings])

  useEffect(() => {
    if (!isDesktop || h2Headings.length === 0) return
    let ticking = false
    const update = () => {
      const offset = getAnchorOffset() + 10
      const y = window.scrollY + offset
      let current = ''
      for (let i = h2Headings.length - 1; i >= 0; i--) {
        const { id } = h2Headings[i]
        const nodes = document.querySelectorAll(`#${CSS.escape(id)}`)
        if (nodes.length) {
          const el = nodes[nodes.length - 1] as HTMLElement
          const top = el.getBoundingClientRect().top + window.scrollY
          if (y >= top) {
            current = id
            break
          }
        }
      }
      if (!current && window.scrollY < 100 && h2Headings.length)
        current = h2Headings[0].id
      setActiveId(prev => (current && current !== prev ? current : prev))
      ticking = false
    }
    const onScroll = () => {
      if (!ticking) {
        requestAnimationFrame(update)
        ticking = true
      }
    }
    update()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [getAnchorOffset, h2Headings, isDesktop])

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault()
    e.stopPropagation()
    window.history.replaceState(null, '', `#${id}`)
    setActiveId(id)
    scrollToId(id, true)
    if (!isDesktop) setIsExpanded(false)
  }

  if (!h2Headings.length) return null

  /* `.toc` from the article reference: a hairline rail with the active item
     marked by a magenta left border. The scroll-spy above is unchanged —
     only the presentation moved. On narrow screens the rail collapses into
     the reference's `.mobile-toc` disclosure. */
  return (
    <div className={cn('not-prose w-full', className)}>
      <span className='article-toc-title'>On this page</span>
      <nav aria-label='Table of contents'>
        <button
          type='button'
          onClick={() => {
            if (isDesktop) return
            setIsExpanded(prev => !prev)
          }}
          className='instrument-label flex w-full items-center justify-between gap-3 py-3 text-left text-muted-foreground xl:hidden'
          aria-expanded={isDesktop ? true : isExpanded}
          aria-controls={listId}
        >
          On this page
          <span
            className={cn(
              'transition-transform duration-150',
              isExpanded ? 'rotate-180' : 'rotate-0'
            )}
            aria-hidden='true'
          >
            ▾
          </span>
        </button>

        <div
          id={listId}
          className={cn(
            'grid gap-0.5 border-l border-line overflow-hidden transition-[max-height,opacity] duration-200 ease-out',
            isExpanded
              ? 'max-h-[75vh] opacity-100'
              : 'max-h-0 opacity-0 pointer-events-none',
            'xl:max-h-none xl:opacity-100 xl:pointer-events-auto xl:overflow-visible'
          )}
          aria-hidden={!isDesktop && !isExpanded}
        >
          {h2Headings.map(({ id, text }) => {
            const isActive = activeId === id
            return (
              <a
                key={id}
                href={`#${id}`}
                onClick={e => handleClick(e, id)}
                className={cn(
                  'block py-[7px] pl-[13px] text-[10px] leading-[1.45] transition-colors',
                  isActive
                    ? '-ml-px border-l-2 border-[var(--cta)] text-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                )}
                aria-current={isActive ? 'location' : undefined}
                title={text}
              >
                {text}
              </a>
            )
          })}
        </div>
      </nav>
    </div>
  )
}

export default TableOfContents
